import { FunctionComponent, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog.tsx'
import { useTranslation } from 'react-i18next'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader.tsx'
import DialogBody from '@/components/_custom/DialogBody/DialogBody.tsx'
import styles from './confirmDeleteOneReservationDialog.module.scss'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { Close, Delete } from '@mui/icons-material'
import { useMutation } from '@apollo/client'
import { DELETE_ONE_RESERVATION_EVENT } from '@/modules/API/daycare/eventDayHandler.ts'
import TextArea from '@/components/_custom/TextArea/textArea.tsx'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface ConfirmDeleteOneReservationProps {
    open: boolean
    handleClose: () => void
    daycareId: number
    eventId: number
    reservationId: number
    reservationEventId: number
    childOrAdultId: number
    isParentForDelete: boolean
    refetchAllReservations: () => Promise<void>
}

const ConfirmDeleteOneReservationDialog: FunctionComponent<ConfirmDeleteOneReservationProps> = ({
    open,
    handleClose,
    daycareId,
    eventId,
    reservationId,
    reservationEventId,
    childOrAdultId,
    isParentForDelete,
    refetchAllReservations
}: ConfirmDeleteOneReservationProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [daycareComment, setDaycareComment] = useState<string>('')

    const [deleteOneReservation] = useMutation(DELETE_ONE_RESERVATION_EVENT, {
        variables: {
            daycareId: daycareId,
            childId: isParentForDelete ? undefined : childOrAdultId,
            parentId: isParentForDelete ? childOrAdultId : undefined,
            reservationId: reservationEventId,
            reservationChildId: isParentForDelete ? undefined : reservationId,
            reservationParentId: isParentForDelete ? reservationId : undefined,
            eventId: eventId,
            comment: daycareComment
        },
        onCompleted: () => {
            refetchAllReservations()

            showNotificationSuccess(t('reservationDeleted'))

            handleClose()
        },
        onError: () => {
            showNotificationError(t('errorReservationDeleted'))
            handleClose()
        }
    })

    return (
        <Dialog open={open} handleClose={handleClose} className={styles.Dialog}>
            <DialogHeader title={t('deleteReservation')} />
            <DialogBody className={styles.DialogBody}>
                <p className={styles.Text}>{t('deleteReservationMessage')}</p>
                <div className={styles.TextAreaInput}>
                    <p className={styles.TextAreaTitle}>{t('unbookReason')}</p>
                    <TextArea
                        placeholder={t('unbookReason')}
                        maxCount={200}
                        className={styles.TextArea}
                        value={daycareComment}
                        onChange={setDaycareComment}
                    />
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon
                        type={'custom'}
                        customText={t('yes')}
                        customIcon={<Delete />}
                        onClick={deleteOneReservation}
                        className={styles.Button}
                    />
                    <ButtonTextIcon
                        type={'custom'}
                        customText={t('no')}
                        customIcon={<Close />}
                        onClick={handleClose}
                        className={styles.Button}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default ConfirmDeleteOneReservationDialog
